import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  FormControlLabel,
  InputAdornment,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { GridSearchIcon } from '@mui/x-data-grid';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import {
  CalendarSyncPair,
  CalendarSyncResponse,
  CalendarSyncStatus,
  getCalendarSyncPairsAPI,
  syncCalendarAPI,
} from 'services/selfSchedulingServices';
import COLORS from 'theme/colors';
import { AutoRefreshCountdown, useAutoRefresh } from './AutoRefresh';
import SyncPairRow from './SyncPairRow';
import { groupByLocation, pairKey } from './syncCalendarGroups';

/** MM-1168: tracking columns between the name column and Action (see SyncPairRow). */
const SYNC_COLUMNS = [
  'Profiles',
  'Profile changed',
  'Rebuild status',
  'Error message',
  'Last started',
  'Last completed',
];

/** MM-1168: shown when ehr-sync gave no usable answer (network error, gateway timeout, 403). */
const GENERIC_SYNC_ERROR = 'Sync failed. Nothing was changed. Please try again.';

/** MM-1168: how often a still-running sync is re-checked. */
const POLL_INTERVAL_MS = 5000;

/** MM-1168: how long a still-running sync is re-checked before leaving it to the auto refresh. */
const POLL_LIMIT_MS = 5 * 60 * 1000;

/**
 * MM-1168: the toast for a rebuild that finished after the sync request answered, from the pair's
 * stored status; still running after the polling limit stays IN_PROGRESS.
 */
const finishedOutcome = (pair?: CalendarSyncPair): { status: CalendarSyncStatus; message: string } => {
  if (pair?.rebuildStatus === 'COMPLETED') {
    return { status: 'SYNCED', message: 'Calendar synced with DrChrono. Self-scheduling availability is up to date.' };
  }
  if (pair?.rebuildStatus === 'COMPLETED_WITH_ERRORS') {
    return {
      status: 'SYNCED_WITH_ERRORS',
      message: pair.rebuildMessage ?? 'Synced, but some profile mappings failed.',
    };
  }
  if (pair?.rebuildStatus === 'FAILED' || pair?.rebuildStatus === 'RETRY_LIMIT_REACHED') {
    return { status: 'FAILED', message: pair.rebuildMessage ?? GENERIC_SYNC_ERROR };
  }
  if (pair?.rebuildStatus === 'PENDING') {
    // MM-1169: still waiting in the rebuild queue (one location + provider at a time)
    return { status: 'QUEUED', message: 'Sync is still queued. The list refreshes automatically.' };
  }
  return { status: 'IN_PROGRESS', message: 'Sync is still running. The list refreshes automatically.' };
};

/**
 * MM-1168: manual DrChrono calendar sync. Lists every office + provider pair self-scheduling keeps
 * free slots for, grouped by location; "Sync Calendar" on a row re-reads that pair's DrChrono
 * calendar (all appointments and breaks over the 90-day window) and rebuilds its self-scheduling
 * availability, so a break added in DrChrono stops being bookable right away. The rebuild status
 * comes straight from the DB (empty = Pending). Each row opens to its profile mappings with their
 * details and their own status / error, so it is clear which profile failed and why.
 */
const SyncCalendar = () => {
  /** Every pair from ehr-sync, null until the first load finishes. */
  const [pairs, setPairs] = useState<CalendarSyncPair[] | null>(null);
  /** True while the pair list is loading. */
  const [loadingPairs, setLoadingPairs] = useState<boolean>(true);
  /** Error text when the pair list could not be loaded. */
  const [loadError, setLoadError] = useState<string>('');
  /** Location / provider name filter. */
  const [search, setSearch] = useState<string>('');
  /** Show only pairs with at least one failed profile mapping. */
  const [errorsOnly, setErrorsOnly] = useState<boolean>(false);
  /** Pairs (pairKey) whose profile mappings are shown. */
  const [expandedPairs, setExpandedPairs] = useState<Record<string, boolean>>({});
  /** Pairs (pairKey) whose sync request from this page is running. */
  const [syncingPairs, setSyncingPairs] = useState<Record<string, boolean>>({});
  /** MM-1198: when the list was last loaded. */
  const [loadedAt, setLoadedAt] = useState<number | null>(null);
  /** MM-1198: true while a load is running, so the auto refresh never starts a second one. */
  const loadingRef = useRef<boolean>(false);

  /**
   * Loads (or reloads) the pair list; resolves with the loaded pairs, or null if the load failed or another load
   * is still running (MM-1198). MM-1169: refreshInventory also re-reads DrChrono's offices / profiles (page load).
   */
  const loadPairs = useCallback(async (refreshInventory = false): Promise<CalendarSyncPair[] | null> => {
    if (loadingRef.current) {
      return null;
    }
    loadingRef.current = true;
    setLoadingPairs(true);
    try {
      const response: any = await getCalendarSyncPairsAPI(refreshInventory);
      const loaded: CalendarSyncPair[] = response?.data ?? [];
      setPairs(loaded);
      setLoadedAt(Date.now());
      setLoadError('');
      return loaded;
    } catch (error: any) {
      console.error('Calendar sync pair load failed', error);
      // MM-1198: no Refresh list button - the next auto refresh tries again
      setLoadError('Could not load locations and providers. Trying again in a few seconds.');
      return null;
    } finally {
      loadingRef.current = false;
      setLoadingPairs(false);
    }
  }, []);

  /** MM-1198: the auto refresh - only re-reads the list (no DrChrono inventory refresh, no sync). */
  const reloadList = useCallback(() => {
    loadPairs();
  }, [loadPairs]);

  /** MM-1198: reloads the list every 10 seconds while the page is open, as on Sync Jobs. */
  const secondsLeft = useAutoRefresh(reloadList);

  /**
   * Reloads the list every POLL_INTERVAL_MS while the pair's rebuild is queued or running (MM-1169: Sync only
   * queues it - the rebuild worker takes one pair at a time), up to POLL_LIMIT_MS.
   * Resolves with the pair as last loaded and whether it had to wait.
   */
  const waitForRebuild = async (pair: CalendarSyncPair, loaded: CalendarSyncPair[] | null) => {
    const isWaiting = (candidate?: CalendarSyncPair) =>
      candidate?.rebuildStatus === 'IN_PROGRESS' || candidate?.rebuildStatus === 'PENDING';
    let current = loaded?.find((candidate) => pairKey(candidate) === pairKey(pair));
    const waited = isWaiting(current);
    const deadline = Date.now() + POLL_LIMIT_MS;
    while (isWaiting(current) && Date.now() < deadline) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
      // eslint-disable-next-line no-await-in-loop
      const reloaded = await loadPairs();
      current = reloaded?.find((candidate) => pairKey(candidate) === pairKey(pair)) ?? current;
    }
    return { current, waited };
  };

  useEffect(() => {
    loadPairs(true);
  }, [loadPairs]);

  /** The visible locations for the current filters. */
  const groups = useMemo(
    () =>
      groupByLocation(
        (pairs ?? []).filter((pair) => !errorsOnly || pair.errorCount > 0),
        search
      ),
    [pairs, search, errorsOnly]
  );

  /** The pairs currently shown. */
  const visiblePairs = useMemo(() => groups.flatMap((group) => group.pairs), [groups]);

  /** Whether every shown pair has its profile mappings open. */
  const allExpanded = visiblePairs.length > 0 && visiblePairs.every((pair) => expandedPairs[pairKey(pair)]);

  /** Opens or closes one pair's profile mappings. */
  const togglePair = (pair: CalendarSyncPair) =>
    setExpandedPairs((previous) => ({ ...previous, [pairKey(pair)]: !previous[pairKey(pair)] }));

  /** Opens every shown pair's profile mappings, or closes them all if they are all open. */
  const toggleAll = () =>
    setExpandedPairs(allExpanded ? {} : Object.fromEntries(visiblePairs.map((pair) => [pairKey(pair), true])));

  /** Marks one pair's sync request as running or done. */
  const setPairSyncing = (pair: CalendarSyncPair, syncing: boolean) =>
    setSyncingPairs((previous) => ({ ...previous, [pairKey(pair)]: syncing }));

  /**
   * Syncs one pair, shows the outcome as a toast and reloads the list so the row's status and
   * dates show what is now stored. Resolves (never rejects) once ehr-sync answered.
   */
  const syncPair = async (pair: CalendarSyncPair) => {
    setPairSyncing(pair, true);
    let result: Partial<CalendarSyncResponse> | undefined;
    try {
      const response: any = await syncCalendarAPI(pair.officeId, pair.providerId);
      result = response?.data;
    } catch (error: any) {
      result = error?.data ?? error?.response?.data;
    }
    const { current, waited } = await waitForRebuild(pair, await loadPairs());
    setPairSyncing(pair, false);
    let status: CalendarSyncStatus = result?.status ?? 'FAILED';
    let message = result?.message ?? GENERIC_SYNC_ERROR;
    if (status === 'QUEUED' || status === 'IN_PROGRESS' || (status === 'ALREADY_RUNNING' && waited)) {
      // the rebuild finished while we waited: report what is now stored
      ({ status, message } = finishedOutcome(current));
    }
    const toastText = `${pair.providerName} - ${pair.officeName}: ${message}`;
    if (status === 'SYNCED') {
      toast.success(toastText);
    } else if (status === 'SYNCED_WITH_ERRORS') {
      toast.warning(toastText);
    } else if (status === 'FAILED') {
      toast.error(toastText);
    } else {
      toast.info(toastText);
    }
  };

  return (
    <Container maxWidth='xl'>
      <Typography variant='h5' sx={{ paddingTop: 6 }}>
        Sync Calendar
      </Typography>
      <Typography variant='body2' color='text.secondary' sx={{ mt: 1, mb: 3 }}>
        Pulls the latest appointments and breaks from DrChrono for a provider at a location and updates self-scheduling
        availability for the next 90 days. Use it after adding, changing or removing a break in DrChrono.
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
        <TextField
          size='small'
          placeholder='Search location or provider'
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <GridSearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 280, backgroundColor: COLORS.White }}
        />
        <FormControlLabel
          control={<Switch checked={errorsOnly} onChange={(event) => setErrorsOnly(event.target.checked)} />}
          label='Errors only'
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ml: 'auto' }}>
          <Button
            variant='outlined'
            size='small'
            startIcon={allExpanded ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
            onClick={toggleAll}
            sx={{ textTransform: 'none' }}
          >
            {allExpanded ? 'Collapse all' : 'Expand all'}
          </Button>
          {/* MM-1198: no Refresh list button - the list reloads itself every 10 seconds (countdown) */}
          <AutoRefreshCountdown loadedAt={loadedAt} secondsLeft={secondsLeft} />
        </Box>
      </Box>

      {loadError ? (
        <Alert severity='error' sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      ) : null}

      {loadingPairs && pairs === null ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ mb: 6 }}>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Location / Provider</TableCell>
                {SYNC_COLUMNS.map((column) => (
                  <TableCell key={column} sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {column}
                  </TableCell>
                ))}
                <TableCell sx={{ fontWeight: 600 }} align='center'>
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={SYNC_COLUMNS.length + 2} align='center' sx={{ py: 4 }}>
                    {search || errorsOnly
                      ? 'No location or provider matches your filters.'
                      : 'No providers are set up for self-scheduling.'}
                  </TableCell>
                </TableRow>
              ) : null}
              {groups.map((group) => (
                <Fragment key={group.key}>
                  <TableRow sx={{ backgroundColor: '#F4F1FF' }}>
                    <TableCell colSpan={SYNC_COLUMNS.length + 2} sx={{ fontWeight: 600 }}>
                      {group.label} ({group.pairs.length})
                    </TableCell>
                  </TableRow>
                  {group.pairs.map((pair) => (
                    <SyncPairRow
                      key={pairKey(pair)}
                      label={pair.providerName}
                      pair={pair}
                      columnCount={SYNC_COLUMNS.length + 2}
                      expanded={!!expandedPairs[pairKey(pair)]}
                      onToggle={() => togglePair(pair)}
                      syncing={!!syncingPairs[pairKey(pair)]}
                      onSync={() => syncPair(pair)}
                    />
                  ))}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
};

export default SyncCalendar;
